import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ArrowLeft } from 'lucide-react';
import { Link } from 'wouter';
import { apiRequest } from '@/lib/queryClient';
import AuthBrand from '@/components/AuthBrand';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast({
        variant: 'destructive',
        title: 'Email required',
        description: 'Please enter your email address.',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await apiRequest('POST', '/api/auth/forgot-password', { email });

      setIsSubmitted(true);
      toast({
        title: 'Reset instructions sent',
        description:
          "If an account with that email exists, we've sent password reset instructions.",
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Request failed',
        description: error.message || 'Failed to send password reset email.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-hydro-100 flex items-center justify-center p-4 font-kumbh">
        <div className="w-full max-w-md">
          <AuthBrand subtitle="Corporate wellness through smart hydration" />

          <Card className="border-lilac-300 shadow-lg">
            <CardHeader>
              <CardTitle className="text-deep-800">Check Your Email</CardTitle>
              <CardDescription className="text-deep-500">
                Password reset instructions have been sent to your email address
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                  <span className="text-green-600 text-2xl">✓</span>
                </div>
                <p className="text-sm text-deep-600 mb-4">
                  If an account with <strong>{email}</strong> exists, we've sent password reset
                  instructions to that address.
                </p>
                <p className="text-xs text-deep-500 mb-6">
                  Please check your email and follow the instructions to reset your password. The
                  reset link will expire in 1 hour for security reasons.
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full bg-lilac-100 border-lilac-300 text-deep-700 hover:bg-lilac-200 rounded-full"
                  onClick={() => {
                    setIsSubmitted(false);
                    setEmail('');
                  }}
                  data-testid="button-send-another"
                >
                  Send Another Reset Email
                </Button>

                <Link href="/login">
                  <Button
                    variant="ghost"
                    className="w-full text-deep-600 hover:text-purple-600"
                    data-testid="link-back-login"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Sign In
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-hydro-100 flex items-center justify-center p-4 font-kumbh">
      <div className="w-full max-w-md">
        <AuthBrand subtitle="Corporate wellness through smart hydration" />

        <Card className="border-lilac-300 shadow-lg">
          <CardHeader>
            <CardTitle className="text-deep-800">Forgot Password?</CardTitle>
            <CardDescription className="text-deep-500">
              Enter your email address and we'll send you instructions to reset your password
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-deep-700">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  required
                  data-testid="input-email"
                  className="bg-lilac-50 border-lilac-300"
                />
              </div>

              <div className="space-y-3">
                <Button
                  type="submit"
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white rounded-full"
                  disabled={isSubmitting}
                  data-testid="button-send-reset"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending Reset Email...
                    </>
                  ) : (
                    'Send Reset Instructions'
                  )}
                </Button>

                <Link href="/login">
                  <Button
                    variant="ghost"
                    className="w-full text-deep-600 hover:text-purple-600"
                    data-testid="link-back-login"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Sign In
                  </Button>
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
